import * as React from 'react'
import {ReactChild, ReactElement} from 'react'
import {
  createStyles,
  Table,
  TableFooter,
  TablePagination,
  TableRow,
  Theme,
  withStyles,
  WithStyles
} from '@material-ui/core'
import {Criteria} from '../../type/criteria/criteria'
import autobind from 'autobind-decorator'
import {RootState} from '../../core/redux/reducer/index'
import {PaginateAction} from '../../core/redux/action/paginateAction'
import {connect} from 'react-redux'
import {WithGlobalProgress} from '../../type/lib/withGlobalProgress'
import {WithToast} from '../../type/lib/withToast'
import {LocalStorageEntity} from '../../core/localStorage'
import {withI18n} from "../../core/i18n";
import {compose} from 'redux'
import {WithI18n} from "../../core/i18n/withI18n";
import LinearProgress from "@material-ui/core/LinearProgress/LinearProgress";
import {withGlobalProgress} from "../GlobalProgress";
import {withToast} from "../Toast";

const DatatableContext = React.createContext({});

const styles = (t:Theme) => createStyles({
  tableContainer: {
      position: 'relative',
      maxWidth: '100%',
      overflowX: 'auto',
      overflowY: 'hidden'
  },
  progress: {
    position: 'absolute',
    left: 0,
    right: 0
  },
  paginationLeft: {
    flex: 0
  },
  paginationRight: {
    flex: '1 1 100%'
  }
});

export const datatableConsumer = <P extends object>(
  Component: React.ComponentType<P & IDatatableContext>
): React.SFC<any> => (props: Pick<P, Exclude<keyof P, keyof IDatatableContext>>) => (
  <DatatableContext.Consumer>
    {(ctx: any) => <Component {...props} {...ctx}/>}
  </DatatableContext.Consumer>
)

interface IProps extends WithGlobalProgress,
  WithI18n,
  WithStyles<typeof styles>,
  WithToast {
  name: string
  action: (c?: Criteria) => (dispatch, getState: () => RootState) => Promise<any>
  criteriaDefault?: any;
  dispatch: any
  children: Array<ReactElement<any>>
  onSelect?: (indexes: number[]) => void
  defaultHiddenColumns?: boolean[]
  isExpendRow?: boolean;
  paginationPosition?: "left" | "right"
}

export interface IDatatableColumn {
  label: ReactChild;
}

export interface IDatatableContext {
  name: string
  actions: PaginateAction<any>
  selected: number[]
  onSelect: (indexes: number[]) => void
  columns: IDatatableColumn[]
  publishColumns: (columns: IDatatableColumn[]) => void
  hiddenColumnsIndexes: boolean[]
  onToggleColumnVisibility: (index: number) => void
  isColumnVisible: (index: number) => boolean
  expendedRow?: number
  expendRow: (id: number) => void
  isExpendRow?: boolean;
}

class Datatable extends React.Component<IProps & ReturnType<typeof state2props>, IDatatableContext> {

  private storage: LocalStorageEntity<boolean[]>

  constructor(props) {
    super(props)
    this.storage = new LocalStorageEntity<boolean[]>('datatable_columns_' + this.props.name);
    this.state = {
      name: props.name,
      actions: new PaginateAction<any>(props.name, props.action),
      selected: [],
      onSelect: props.onSelect && this.handleSelect,
      columns: [],
      publishColumns: this.handlePublishColumns,
      hiddenColumnsIndexes: this.getIndexex(),
      onToggleColumnVisibility: this.ToggleColumnVisibility,
      isColumnVisible: this.isColumnVisible,
      expendRow: this.expendRow,
      isExpendRow: props.isExpendRow
    }
  }

  render() {
    const {
      children, criteria, size, page, t, isFetching, classes, paginationPosition
    } = this.props;
    return (
      <DatatableContext.Provider value={this.state}>
        <div className={classes.tableContainer}>
          {isFetching && <LinearProgress className={classes.progress} /> }
          <Table style={{borderCollapse: 'initial' /* Fix bug when apply transform on <tr> */}}>
            {children}
            <TableFooter>
              <TableRow>
                <TablePagination
                  classes={{
                    spacer: paginationPosition === "left" ? classes.paginationLeft : classes.paginationRight
                  }}
                  labelRowsPerPage={t.Table_row_per_page}
                  labelDisplayedRows={this.renderDisplayedRows}
                  count={size || 0}
                  rowsPerPage={criteria.limit}
                  page={page}
                  onChangePage={this.handleChangePage}
                  onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </DatatableContext.Provider>
    )
  }

  componentDidMount() {
    const {data} = this.props
    if (!data) this.search()
  }

  private getIndexex() {
    return Array.isArray(this.storage.load()) ? this.storage.load() : !!this.props.defaultHiddenColumns ? this.props.defaultHiddenColumns : []
  }

  private async search() {
    const {dispatch, criteria, action, promisesWithProgress, toastError, criteriaDefault} = this.props
    promisesWithProgress(
      dispatch(action(criteriaDefault ? criteriaDefault : criteria)).catch(err => toastError(err.msg))
    )
  }

  @autobind
  private renderDisplayedRows({from, to, count}): string {
    const { t } = this.props;
    return t.Table_displayed_rows(from, to, count)
  }

  @autobind
  private handleChangeRowsPerPage(event) {
    const {dispatch} = this.props
    this.resetExpendedRow()
    if (this.state.actions)
      dispatch(this.state.actions.updateCriteria('limit', event.target.value))
  }

  /*
  @autobind
  private handleSortChange(sortBy: string, orderBy: OrderByType) {
    this.resetExpendedRow()
    const {dispatch} = this.props
    dispatch(this.state.actions.sort(sortBy, orderBy))
  }
  */

  @autobind
  private handleChangePage(e, newPage: number) {
    this.resetExpendedRow()
    const {dispatch} = this.props
    if (this.state.actions)
      dispatch(this.state.actions.goToPage(newPage))
  }

  @autobind
  private handleSelect(selected: number[]) {
    const {onSelect} = this.props
    this.setState({selected})
    if (onSelect) onSelect(selected)
  }

  @autobind
  private handlePublishColumns(columns: IDatatableColumn[]) {
    this.setState({columns})
  }

  @autobind
  private isColumnVisible(i: number): boolean {
    const {hiddenColumnsIndexes} = this.state
    return (Array.isArray(hiddenColumnsIndexes) && (hiddenColumnsIndexes[i] !== undefined && hiddenColumnsIndexes[i] !== null)) ? !hiddenColumnsIndexes[i] : true
  }

  @autobind
  private ToggleColumnVisibility(i: number) {
    const {hiddenColumnsIndexes} = this.state
    hiddenColumnsIndexes[i] = !hiddenColumnsIndexes[i]
    this.setState({hiddenColumnsIndexes: [...hiddenColumnsIndexes]}, this.persistHiddenColumns)
  }

  @autobind
  private expendRow(id: number) {
    this.setState(state => ({expendedRow: state.expendedRow !== id ? id : undefined}))
  }

  private resetExpendedRow() {
    if (this.state.expendedRow)
      this.setState({expendedRow: undefined})
  }

  private persistHiddenColumns() {
    this.storage.save(this.state.hiddenColumnsIndexes)
  }
}

const state2props = (state: RootState, ownProps: IProps) => {
  const paginateState = state.paginate[ownProps.name]
  return {
    isFetching: paginateState.isFetching,
    page: paginateState.criteria.offset / paginateState.criteria.limit,
    criteria: paginateState.criteria,
    size: paginateState.total_size,
    data: paginateState.entities,
  }
}

export default compose(
  withI18n,
  withGlobalProgress,
  withToast,
  withStyles(styles),
  connect(state2props)
)(Datatable);



// WEBPACK FOOTER //
// ./src/shared/Datatable/Datatable.tsx